import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { dropPoint } from '@tiptap/pm/transform';
import { EditorView } from '@tiptap/pm/view';

import {
	DROP_CURSOR_CLASS,
	DROP_CURSOR_THICKNESS_PX,
	DROP_CURSOR_VISIBLE_CLASS,
} from './constants';

export type Point = { x: number; y: number };

type Rect = { left: number; top: number; width: number; height: number };

/** Flicker-free `prosemirror-dropcursor`: one element, fixed to the viewport, faded not unmounted. */
export class DropCursorView {
	private element: HTMLElement | null = null;
	private pos: number | null = null;
	private frame = 0;

	constructor(private view: EditorView) {}

	destroy() {
		cancelAnimationFrame(this.frame);
		this.element?.remove();
		this.element = null;
	}

	get isVisible(): boolean {
		return this.pos !== null;
	}

	/** Where the line currently sits, so a drop can land exactly where it was promised. */
	get targetPos(): number | null {
		return this.pos;
	}

	/** Resolves the pointer to the position a drop would actually use, then paints there. */
	showAt(point: Point) {
		const coords = this.view.posAtCoords({ left: point.x, top: point.y });
		if (!coords) return;

		const slice = this.view.dragging?.slice;
		const target = slice ? dropPoint(this.view.state.doc, coords.pos, slice) : coords.pos;
		if (target === null) return;

		this.pos = target;
		this.schedulePaint();
	}

	hide() {
		this.pos = null;
		cancelAnimationFrame(this.frame);
		this.frame = 0;
		this.element?.classList.remove(DROP_CURSOR_VISIBLE_CLASS);
	}

	private schedulePaint() {
		if (this.frame) return;

		this.frame = requestAnimationFrame(() => {
			this.frame = 0;
			this.paint();
		});
	}

	private paint() {
		if (this.pos === null || this.pos > this.view.state.doc.content.size) return;

		const rect = this.rectAt(this.pos);
		const element = this.mount();
		if (!rect || !element) return;

		element.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
		element.style.width = `${rect.width}px`;
		element.style.height = `${rect.height}px`;
		element.classList.add(DROP_CURSOR_VISIBLE_CLASS);
	}

	private mount(): HTMLElement {
		if (this.element) return this.element;

		this.element = document.createElement('div');
		this.element.className = DROP_CURSOR_CLASS;
		document.body.appendChild(this.element);

		return this.element;
	}

	/** A full-width line on the seam between blocks, or a caret inside a paragraph. */
	private rectAt(pos: number): Rect | null {
		const $pos = this.view.state.doc.resolve(pos);
		const seam = $pos.parent.inlineContent
			? null
			: this.seamRect(pos, $pos.nodeBefore, $pos.nodeAfter);

		if (seam) return seam;

		const caret = this.view.coordsAtPos(pos);
		return {
			left: caret.left - DROP_CURSOR_THICKNESS_PX / 2,
			top: caret.top,
			width: DROP_CURSOR_THICKNESS_PX,
			height: caret.bottom - caret.top,
		};
	}

	private seamRect(
		pos: number,
		before: ProseMirrorNode | null,
		after: ProseMirrorNode | null
	): Rect | null {
		if (!before && !after) return null;

		const neighbour = this.view.nodeDOM(pos - (before ? before.nodeSize : 0));
		if (!(neighbour instanceof HTMLElement)) return null;

		const rect = neighbour.getBoundingClientRect();
		const edge = before ? rect.bottom : rect.top;
		const next = before && after ? this.view.nodeDOM(pos) : null;
		// With a node on each side, split the gap so the line reads as belonging to neither.
		const top = next instanceof HTMLElement ? (edge + next.getBoundingClientRect().top) / 2 : edge;

		return {
			left: rect.left,
			top: top - DROP_CURSOR_THICKNESS_PX / 2,
			width: rect.width,
			height: DROP_CURSOR_THICKNESS_PX,
		};
	}
}
